import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import Navbar from '../../components/Navbar/Navbar';
import { useNavigate } from 'react-router-dom';
import { Gavel, MessageSquare, Scale, Filter, Award } from 'lucide-react';
import './BrowseQueries.css';

const CATEGORY_TABS = [
  "All",
  "#ClientFromHell",
  "#MedicalMadness",
  "#DesignDisaster",
  "#EngineeringChaos",
  "#EditingNightmare",
  "#FriendDispute"
];

function BrowseQueries() {
  const [activeTab, setActiveTab] = useState('docket'); // 'docket' or 'vault'
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchCourtCases = async () => {
    try {
      setLoading(true);
      const casesRef = collection(db, 'cases');
      let q = query(casesRef, orderBy('createdAt', 'desc'), limit(30));
      let snap = await getDocs(q);

      let list = [];
      snap.forEach(docSnap => {
        list.push({ id: docSnap.id, ...docSnap.data() });
      });

      // Only load from cases collection (excludes legacy AskAway queries)
      setCases(list);
    } catch (err) {
      console.error('Error fetching docket cases:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourtCases();
  }, []);

  // Filter based on active tab and category
  const filteredCases = cases.filter(c => {
    // Tab filter
    if (activeTab === 'vault') {
      const isVaulted = c.status === 'vaulted' || (c.totalVotes >= 5 && c.aiJudgeRuling);
      if (!isVaulted) return false;
    } else {
      if (c.status === 'vaulted') return false;
    }

    // Category filter
    if (selectedCategory !== 'All') {
      const cat = c.category || (c.tags && c.tags[0]) || '';
      if (!cat.toLowerCase().includes(selectedCategory.toLowerCase().replace('#', ''))) {
        return false;
      }
    }

    return true;
  });

  const getLeadingVerdict = (verdictCounts = {}) => {
    let topVerdict = null;
    let maxCount = 0;
    Object.entries(verdictCounts).forEach(([v, count]) => {
      if (count > maxCount) {
        maxCount = count;
        topVerdict = v;
      }
    });
    return topVerdict ? { name: topVerdict.replace(/_/g, ' '), count: maxCount } : null;
  };

  return (
    <div className="docket-page">
      <Navbar />
      <div className="docket-container">
        
        {/* Court Banner */}
        <div className="court-hero">
          <div className="hero-emblem">
            <Gavel size={36} />
          </div>
          <h1>The Kangaroo Court of the Syndicate</h1>
          <p>
            Private jurisdiction for workplace blunders, unhinged clients, medical mysteries, and friend trials.
          </p>

          {/* Primary View Switcher */}
          <div className="court-tab-switcher">
            <button
              className={`court-tab-btn ${activeTab === 'docket' ? 'active' : ''}`}
              onClick={() => setActiveTab('docket')}
            >
              <Scale size={18} />
              Active Docket (Open Trials)
            </button>
            <button
              className={`court-tab-btn ${activeTab === 'vault' ? 'active' : ''}`}
              onClick={() => setActiveTab('vault')}
            >
              <Award size={18} />
              The Vault of Shame (Hall of Infamy)
            </button>
          </div>
        </div>

        {/* Discipline / Category Filter Bar */}
        <div className="category-filter-bar">
          <div className="filter-label">
            <Filter size={15} />
            <span>Jurisdiction:</span>
          </div>
          <div className="filter-pills-row">
            {CATEGORY_TABS.map(cat => (
              <button
                key={cat}
                className={`filter-pill ${selectedCategory === cat ? 'selected' : ''}`}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Cases Grid */}
        {loading ? (
          <div className="court-loading-state">
            <div className="court-spinner"></div>
            <p>Retrieving Court Dockets from the Archives...</p>
          </div>
        ) : filteredCases.length === 0 ? (
          <div className="court-empty-docket">
            <Gavel size={48} className="empty-docket-icon" />
            <h3>No Cases Found in this Jurisdiction</h3>
            <p>
              {activeTab === 'vault' 
                ? 'No cases have been inducted into the Vault of Shame yet. Vote on the active docket!' 
                : 'The docket is clear! Have an unhinged client or workplace disaster? File a case before the Bar.'}
            </p>
            <button className="btn-empty-file" onClick={() => navigate('/post')}>
              File a New Docket (+25 Chips)
            </button>
          </div>
        ) : (
          <div className="docket-cards-grid">
            {filteredCases.map((c, index) => {
              const leading = getLeadingVerdict(c.verdictCounts);
              const caseNum = c.caseNumber || (101 + index);

              return (
                <div
                  key={c.id}
                  className={`docket-case-card ${c.aiJudgeRuling ? 'has-ruling' : ''}`}
                  onClick={() => navigate(`/query/${c.id}`)}
                >
                  <div className="card-top-row">
                    <span className="case-id-tag">Case #{caseNum}</span>
                    <span className="case-category-tag">
                      {c.category || (c.tags && c.tags[0]) || '#Disaster'}
                    </span>
                    {c.aiJudgeRuling && (
                      <span className="ruling-badge">
                        <Gavel size={12} />
                        Decree Rendered
                      </span>
                    )}
                  </div>

                  <h3 className="case-card-title">{c.title}</h3>
                  <p className="case-card-snippet">
                    {c.description ? c.description.substring(0, 160) + '...' : 'No details provided.'}
                  </p>

                  {leading && (
                    <div className="leading-verdict-pill">
                      <span className="leading-label">Leading Jury Verdict:</span>
                      <strong>{leading.name} ({leading.count} votes)</strong>
                    </div>
                  )}

                  <div className="card-footer-row">
                    <span className="case-author-alias">
                      {c.isAnonymous ? '🛡️ Filed Under Seal' : `By ${c.authorAlias || c.authorName || 'Juror'}`}
                    </span>
                    <div className="case-meta-stats">
                      <span title="Total Juror Votes">
                        <Scale size={14} />
                        {c.totalVotes || 0}
                      </span>
                      <span title="Deliberations / Roasts">
                        <MessageSquare size={14} />
                        {c.commentCount || 0}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default BrowseQueries;
